import { api, unwrap } from "@lumberset/api-client";
import { HelpMarkdown } from "@lumberset/support-ui";
import { date } from "@lumberset/takeoff-ui";
import {
  Button,
  Container,
  EmptyState,
  Field,
  Input,
  Prompt,
  SectionHeader,
  Select,
  Table,
  Textarea,
  toast,
} from "@lumberset/ui";
import { BellAlert, MagnifyingGlass } from "@medusajs/icons";
import { useMutation, useQuery, useQueryClient, useSuspenseQuery } from "@tanstack/react-query";
import { createFileRoute } from "@tanstack/react-router";
import { type FormEvent, useDeferredValue, useState } from "react";
import { Crumbs } from "../../components/crumbs";
import { announcementsQuery, yardsQuery } from "../../lib/queries";

export const Route = createFileRoute("/_app/announcements")({
  loader: ({ context: { queryClient } }) => queryClient.ensureQueryData(announcementsQuery),
  component: Announcements,
});

type Audience = "all" | "yards" | "builders" | "staff" | "workspace";

const audienceLabels: Record<Audience, string> = {
  all: "Everyone: owners and managers of every workspace, plus Staff",
  yards: "Yards: owners and managers of every yard",
  builders: "Builders: owners and managers of every builder",
  staff: "Staff only",
  workspace: "One workspace: every member of it",
};

const shortAudience: Record<Audience, string> = {
  all: "Everyone",
  yards: "Yards",
  builders: "Builders",
  staff: "Staff",
  workspace: "One workspace",
};

function Announcements() {
  const queryClient = useQueryClient();
  const { data } = useSuspenseQuery(announcementsQuery);
  const [subject, setSubject] = useState("");
  const [body, setBody] = useState("");
  const [audience, setAudience] = useState<Audience>("all");
  const [yardId, setYardId] = useState<string | null>(null);
  const [yardText, setYardText] = useState("");
  const yardQ = useDeferredValue(yardText.trim().toLowerCase());
  const [confirming, setConfirming] = useState(false);

  const yards = useQuery({ ...yardsQuery, enabled: audience === "workspace" });
  const matches = (yards.data?.yards ?? [])
    .filter((yard) => !yardQ || yard.name.toLowerCase().includes(yardQ))
    .slice(0, 8);
  const chosenYard = yards.data?.yards.find((yard) => yard.id === yardId) ?? null;

  const target = { audience, yardId: audience === "workspace" ? yardId : null };
  const count = useQuery({
    queryKey: ["admin", "announcements", "count", target],
    queryFn: () => unwrap(api.admin.email.announcements.count.$post({ json: target })),
    enabled: audience !== "workspace" || yardId !== null,
  });

  const send = useMutation({
    mutationFn: () =>
      unwrap(api.admin.email.announcements.$post({ json: { ...target, subject, body } })),
    onSuccess: async ({ sent, failed }) => {
      await queryClient.invalidateQueries({ queryKey: ["admin", "announcements"] });
      setConfirming(false);
      setSubject("");
      setBody("");
      toast.success(`Sent to ${sent} ${sent === 1 ? "person" : "people"}`, {
        description: failed > 0 ? `${failed} could not be delivered.` : undefined,
      });
    },
    onError: (error) => {
      setConfirming(false);
      toast.error(error.message);
    },
  });

  const ready =
    subject.trim().length > 0 &&
    body.trim().length > 0 &&
    (audience !== "workspace" || yardId !== null) &&
    (count.data?.count ?? 0) > 0;

  function submit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (ready) setConfirming(true);
  }

  return (
    <>
      <Crumbs trail={[{ label: "Announcements" }]} />

      <form onSubmit={submit} className="flex flex-col gap-y-3">
        <Container className="divide-y p-0">
          <SectionHeader
            level="h1"
            title="Send an announcement"
            description="One email to many people at once. Plain text or markdown; links come through as text and address."
          />
          <div className="grid grid-cols-1 gap-x-4 gap-y-5 px-6 py-5 lg:grid-cols-2">
            <div className="flex flex-col gap-y-5">
              <Field label="Who gets it">
                {(control) => (
                  <Select
                    value={audience}
                    onValueChange={(value) => {
                      setAudience(value as Audience);
                      setYardId(null);
                    }}
                  >
                    <Select.Trigger id={control.id}>
                      <Select.Value />
                    </Select.Trigger>
                    <Select.Content>
                      {(Object.keys(audienceLabels) as Audience[]).map((value) => (
                        <Select.Item key={value} value={value}>
                          {audienceLabels[value]}
                        </Select.Item>
                      ))}
                    </Select.Content>
                  </Select>
                )}
              </Field>

              {audience === "workspace" ? (
                <Field label="Workspace">
                  {(control) => (
                    <div className="flex flex-col gap-y-2">
                      <Input
                        {...control}
                        type="search"
                        prefix={<MagnifyingGlass />}
                        placeholder="Search by name"
                        value={yardText}
                        onChange={(event) => setYardText(event.target.value)}
                      />
                      {chosenYard ? (
                        <p className="txt-compact-small text-ui-fg-base">
                          Sending to <span className="font-medium">{chosenYard.name}</span>
                        </p>
                      ) : null}
                      <ul className="flex flex-col divide-y divide-ui-border-base rounded-lg border border-ui-border-base">
                        {matches.length === 0 ? (
                          <li className="txt-small px-3 py-2 text-ui-fg-muted">
                            {yards.isPending ? "Loading workspaces" : "No workspace matches"}
                          </li>
                        ) : (
                          matches.map((yard) => (
                            <li key={yard.id}>
                              <button
                                type="button"
                                data-active={yard.id === yardId ? "true" : undefined}
                                className="transition-fg flex w-full cursor-pointer items-center justify-between gap-x-3 px-3 py-2 text-left outline-none hover:bg-ui-bg-base-hover focus-visible:shadow-borders-focus data-[active=true]:bg-ui-bg-subtle"
                                onClick={() => setYardId(yard.id)}
                              >
                                <span className="txt-compact-small-plus truncate text-ui-fg-base">
                                  {yard.name}
                                </span>
                                <span className="txt-compact-xsmall shrink-0 text-ui-fg-muted">
                                  {yard.billingPlanName ?? "No Billing Plan"}
                                </span>
                              </button>
                            </li>
                          ))
                        )}
                      </ul>
                    </div>
                  )}
                </Field>
              ) : null}

              <Field label="Subject">
                {(control) => (
                  <Input
                    {...control}
                    required
                    maxLength={200}
                    value={subject}
                    onChange={(event) => setSubject(event.target.value)}
                  />
                )}
              </Field>
              <Field label="Message" hint="Leave a blank line between paragraphs.">
                {(control) => (
                  <Textarea
                    {...control}
                    required
                    rows={10}
                    maxLength={20_000}
                    value={body}
                    onChange={(event) => setBody(event.target.value)}
                  />
                )}
              </Field>
            </div>

            <div className="flex flex-col gap-y-2">
              <p className="txt-compact-small-plus text-ui-fg-base">Preview</p>
              <div className="flex min-h-[200px] flex-col gap-y-3 rounded-lg border border-ui-border-base bg-ui-bg-base px-5 py-4">
                <p className="h2-core text-ui-fg-base">{subject || "Subject"}</p>
                {body.trim() ? (
                  <HelpMarkdown markdown={body} />
                ) : (
                  <p className="txt-small text-ui-fg-muted">
                    Your message appears here as you type.
                  </p>
                )}
                <p className="txt-small text-ui-fg-muted">
                  You are receiving this because you have a LumberSet account.
                </p>
              </div>
            </div>
          </div>
          <div className="flex flex-wrap items-center justify-end gap-3 px-6 py-4">
            <span className="txt-compact-small text-ui-fg-muted">
              {audience === "workspace" && yardId === null
                ? "Choose a workspace"
                : count.isPending
                  ? "Counting people"
                  : count.data
                    ? `${count.data.count} ${count.data.count === 1 ? "person" : "people"} would get it`
                    : ""}
            </span>
            <Button type="submit" disabled={!ready} isLoading={send.isPending}>
              <BellAlert />
              {ready && count.data
                ? `Send to ${count.data.count} ${count.data.count === 1 ? "person" : "people"}`
                : "Send"}
            </Button>
          </div>
        </Container>
      </form>

      <Container className="divide-y p-0">
        <SectionHeader title="Sent" description="Every announcement, newest first." />
        {data.announcements.length === 0 ? (
          <EmptyState
            icon={<BellAlert />}
            title="Nothing sent yet"
            description="Announcements you send appear here with who got them."
          />
        ) : (
          <Table>
            <Table.Header>
              <Table.Row>
                <Table.HeaderCell>Subject</Table.HeaderCell>
                <Table.HeaderCell>To</Table.HeaderCell>
                <Table.HeaderCell className="max-md:hidden">Sent by</Table.HeaderCell>
                <Table.HeaderCell className="text-right">People</Table.HeaderCell>
                <Table.HeaderCell className="max-sm:hidden">When</Table.HeaderCell>
              </Table.Row>
            </Table.Header>
            <Table.Body>
              {data.announcements.map((announcement) => (
                <Table.Row key={announcement.id}>
                  <Table.Cell>
                    <div className="txt-compact-small-plus max-w-[45vw] truncate text-ui-fg-base">
                      {announcement.subject}
                    </div>
                    <div className="txt-compact-xsmall text-ui-fg-muted sm:hidden">
                      {date(announcement.sentAt ?? announcement.createdAt)}
                    </div>
                  </Table.Cell>
                  <Table.Cell className="whitespace-nowrap">
                    {announcement.audience === "workspace"
                      ? (announcement.yardName ?? "One workspace")
                      : shortAudience[announcement.audience as Audience]}
                  </Table.Cell>
                  <Table.Cell className="whitespace-nowrap max-md:hidden">
                    {announcement.sentByName ?? "-"}
                  </Table.Cell>
                  <Table.Cell className="tabular text-right">
                    {announcement.recipientCount}
                  </Table.Cell>
                  <Table.Cell className="whitespace-nowrap max-sm:hidden">
                    {date(announcement.sentAt ?? announcement.createdAt)}
                  </Table.Cell>
                </Table.Row>
              ))}
            </Table.Body>
          </Table>
        )}
      </Container>

      <Prompt open={confirming} onOpenChange={setConfirming}>
        <Prompt.Content>
          <Prompt.Header>
            <Prompt.Title>
              Send to {count.data?.count ?? 0} {count.data?.count === 1 ? "person" : "people"}?
            </Prompt.Title>
            <Prompt.Description>
              "{subject}" goes out by email to{" "}
              {audience === "workspace"
                ? `every member of ${chosenYard?.name ?? "the workspace"}`
                : shortAudience[audience].toLowerCase()}
              . It cannot be recalled.
            </Prompt.Description>
          </Prompt.Header>
          <Prompt.Footer>
            <Prompt.Cancel>Not yet</Prompt.Cancel>
            <Prompt.Action tone="primary" onClick={() => send.mutate()}>
              Send
            </Prompt.Action>
          </Prompt.Footer>
        </Prompt.Content>
      </Prompt>
    </>
  );
}
